// filepath: /Users/mpilone/VsCodeProjects/terrasense/frontend/src/services/api.js
import axios from 'axios';
import router from '../router';

// Create Axios instance with default config
const API_URL = process.env.VUE_APP_API_URL || '/api';
export const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 30000, // 30 seconds timeout for production
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Auth service reference that will be set later
let authService = null;

// Track if we're currently refreshing the token
let isRefreshing = false;
// Queue of requests to retry after token refresh
let refreshQueue = [];

/**
 * Set the auth service to be used by the API client
 * @param {Object} service - The auth service instance
 */
export const setAuthService = (service) => {
  authService = service;
};

/**
 * Get the current auth service
 * @returns {Object} The auth service instance
 */
export const getAuthService = () => {
  return authService;
};

// Process the refresh queue with a new token
const processRefreshQueue = (token, error = null) => {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  refreshQueue = [];
};

// Request interceptor to automatically add the authentication token
apiClient.interceptors.request.use(
  async (config) => {
    // Skip adding token for auth endpoints
    if (config.url?.includes('/auth/') && !config.url?.includes('/refresh')) {
      return config;
    }

    try {
      if (!authService) {
        // If auth service hasn't been set yet, proceed without token
        return config;
      }
      
      const token = await authService.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token expiration and refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If the error is not related to authentication or the request has already been retried
    // or auth service is not initialized yet
    if (error.response?.status !== 401 || originalRequest._retry || !authService) {
      return Promise.reject(error);
    }
    
    // Mark request as retried to prevent infinite loops
    originalRequest._retry = true;

    // If we're already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    }

    // Start refreshing
    isRefreshing = true;
    
    try {
      // Attempt to get a fresh token
      const token = await authService.getToken();
      
      // If successful, process the queue and retry the original request
      processRefreshQueue(token);
      originalRequest.headers.Authorization = `Bearer ${token}`;
      isRefreshing = false;
      
      return apiClient(originalRequest);
    } catch (refreshError) {
      // If refresh fails, process the queue with an error and redirect to login
      processRefreshQueue(null, refreshError);
      isRefreshing = false;
      
      // Redirect to login page if not already there
      if (router.currentRoute.value.path !== '/login' && 
          router.currentRoute.value.path !== '/') {
        router.push('/login');
      }
      
      return Promise.reject(refreshError);
    }
  }
);
